// src/quiz/gre-mock5/quantitativeData.js
// GRE Mock 5 — Quantitative Reasoning (hardest set so far).
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

const SALES_TABLE = {
  caption: "Annual Sales of Company Q by Region (in millions of dollars)",
  headers: ["Region", "2022", "2023"],
  rows: [
    ["North", "40", "46"],
    ["South", "25", "30"],
    ["East", "50", "45"],
    ["West", "35", "49"],
    ["Total", "150", "170"],
  ],
};

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    given: "0 < x < 1",
    quantityA: "√x − x",
    quantityB: "x − x²",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "Try values. x = 0.01: A = 0.1 − 0.01 = 0.09, B = 0.01 − 0.0001 = 0.0099, so A is greater. x = 0.81: A = 0.9 − 0.81 = 0.09, B = 0.81 − 0.6561 = 0.1539, so B is greater. Knowing only that x is between 0 and 1 does not settle the comparison.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    quantityA: "The number of positive integers less than 100 that are divisible by 6 or by 15",
    quantityB: "20",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Multiples of 6 below 100: 16. Multiples of 15: 6. Multiples of both (i.e. of 30) are counted twice: 3. Total 16 + 6 − 3 = 19, which is less than 20.",
  },
  {
    id: 3,
    question: QC_QUESTION,
    given: "When the positive integer k is divided by 6, the remainder is 5.",
    quantityA: "The remainder when k² is divided by 9",
    quantityB: "4",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "k = 5 gives k² = 25, remainder 7 (A greater). k = 11 gives k² = 121 = 13 × 9 + 4, remainder 4 (equal). Two valid values give different relationships.",
  },
  {
    id: 4,
    question: QC_QUESTION,
    given: "In triangle ABC, AB = 7 and BC = 10.",
    quantityA: "The area of triangle ABC",
    quantityB: "35",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "Area = ½ × 7 × 10 × sin B, which is at most ½ × 7 × 10 = 35, reached only when angle B is a right angle. The area can equal 35 or be less, so the relationship cannot be determined. \"B is greater\" is the trap.",
  },
  {
    id: 5,
    question: "If 4ˣ + 4ˣ + 4ˣ + 4ˣ = 2^(3x − 1), what is the value of x?",
    options: ["1", "2", "3", "4", "5"],
    answer: "3",
    explanation: "The left side is 4 × 4ˣ = 4^(x + 1) = 2^(2x + 2). Setting 2x + 2 = 3x − 1 gives x = 3.",
  },
  {
    id: 6,
    question: "A jar contains only red and blue marbles in the ratio 3 to 5. If 12 red marbles are added and 4 blue marbles are removed, the jar contains equal numbers of red and blue marbles. How many marbles were originally in the jar?",
    options: ["40", "48", "56", "64", "72"],
    answer: "64",
    explanation: "Let red = 3k and blue = 5k. Then 3k + 12 = 5k − 4, so k = 8: 24 red and 40 blue, 64 in all. Check: 36 red and 36 blue afterward.",
  },
  {
    id: 7,
    type: "numeric",
    question: "How many integers n satisfy |n² − 20| ≤ 16?",
    answer: 10,
    explanation: "The inequality means −16 ≤ n² − 20 ≤ 16, i.e. 4 ≤ n² ≤ 36, so 2 ≤ |n| ≤ 6. That is n = ±2, ±3, ±4, ±5, ±6: 10 integers. Forgetting the negative values gives the trap answer 5.",
  },
  {
    id: 8,
    type: "multi",
    question: "If xy < 0 and x + y > 0, which of the following must be true?",
    options: ["x > 0", "x³ + y³ > 0", "(x + y)² > xy", "|x + y| < |x| + |y|", "x/y < −1"],
    answer: ["x³ + y³ > 0", "(x + y)² > xy", "|x + y| < |x| + |y|"],
    explanation: "x and y have opposite signs and the positive one is larger in size, but either could be the positive one, so \"x > 0\" and \"x/y < −1\" need not hold (try x = −1, y = 3). x³ + y³ = (x + y)(x² − xy + y²) is a product of two positives. (x + y)² > 0 > xy. With opposite signs, |x + y| is strictly less than |x| + |y|.",
  },
  {
    id: 9,
    question: "A circle is inscribed in an equilateral triangle with side length 6. What is the area of the region inside the triangle but outside the circle?",
    options: ["6√3 − 3π", "9√3 − 3π", "9√3 − π", "9√3 − 9π/4", "18√3 − 3π"],
    answer: "9√3 − 3π",
    explanation: "Triangle area = (√3/4) × 36 = 9√3. The inradius of an equilateral triangle is side/(2√3) = 6/(2√3) = √3, so the circle's area is 3π. Difference: 9√3 − 3π.",
  },
  {
    id: 10,
    table: SALES_TABLE,
    question: "By approximately what percent did the West region's share of Company Q's total sales increase from 2022 to 2023?",
    options: ["5.5%", "14%", "24%", "33%", "40%"],
    answer: "24%",
    explanation: "West's share was 35/150 ≈ 23.3% in 2022 and 49/170 ≈ 28.8% in 2023. The percent increase in the share is 28.8/23.3 − 1 ≈ 24%. 5.5% is the change in percentage points; 40% is the increase in West's sales, not its share.",
  },
  {
    id: 11,
    type: "numeric",
    table: SALES_TABLE,
    question: "If the East region's sales change from 2023 to 2024 by the same percent as they did from 2022 to 2023, what will its 2024 sales be, in millions of dollars?",
    answer: 40.5,
    explanation: "East fell from 50 to 45, a 10% decrease. Another 10% decrease gives 45 × 0.9 = 40.5. Subtracting the same 5 million (to get 40) is the trap.",
  },
  {
    id: 12,
    type: "multi",
    table: SALES_TABLE,
    question: "For which regions was the share of Company Q's total sales greater in 2023 than in 2022?",
    options: ["North", "South", "East", "West"],
    answer: ["North", "South", "West"],
    explanation: "North 40/150 ≈ 26.7% → 46/170 ≈ 27.1%; South 16.7% → 17.6%; East 33.3% → 26.5%; West 23.3% → 28.8%. North's share rose only slightly, but it did rise. Only East's fell.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: QC_QUESTION,
    given: "2ⁿ = 3",
    quantityA: "4ⁿ + 8ⁿ",
    quantityB: "36",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "4ⁿ = (2ⁿ)² = 9 and 8ⁿ = (2ⁿ)³ = 27, so Quantity A is 36.",
  },
  {
    id: 14,
    question: QC_QUESTION,
    quantityA: "√15 + √17",
    quantityB: "8",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Square both: A² = 15 + 17 + 2√(15 × 17) = 32 + 2√255, and B² = 64 = 32 + 2√256. Since √255 < √256, A < B.",
  },
  {
    id: 15,
    question: QC_QUESTION,
    given: "The average (arithmetic mean) of five different positive integers is 10.",
    quantityA: "The greatest possible value of the largest of the five integers",
    quantityB: "40",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "The sum is 50. To make the largest as big as possible, make the other four as small as possible while keeping them different: 1 + 2 + 3 + 4 = 10. The largest is then 50 − 10 = 40.",
  },
  {
    id: 16,
    question: QC_QUESTION,
    quantityA: "The standard deviation of the list 1, 3, 5, 7, 9",
    quantityB: "The standard deviation of the list 11, 12, 13, 14, 15",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "Adding a constant does not change the spread, so list B has the same standard deviation as 1, 2, 3, 4, 5. List A is that list multiplied by 2 and shifted, so its standard deviation is twice as large.",
  },
  {
    id: 17,
    question: QC_QUESTION,
    given: "A cube has a total surface area of 54.",
    quantityA: "The length of a diagonal joining two opposite vertices of the cube",
    quantityB: "5",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "6s² = 54 gives s = 3. The space diagonal is s√3 = 3√3 = √27 ≈ 5.20, which is greater than √25 = 5.",
  },
  {
    id: 18,
    question: "The sum of the first n positive odd integers is n². What is the sum of all the odd integers from 21 to 59, inclusive?",
    options: ["760", "780", "800", "820", "840"],
    answer: "800",
    explanation: "1 + 3 + … + 59 is the sum of the first 30 odd integers: 900. 1 + 3 + … + 19 is the first 10: 100. The difference is 800.",
  },
  {
    id: 19,
    question: "A committee of 3 people is to be chosen at random from 5 men and 4 women. What is the probability that the committee includes at least one woman?",
    options: ["5/42", "4/9", "37/42", "20/21", "41/42"],
    answer: "37/42",
    explanation: "Use the complement. All-male committees: C(5, 3) = 10 out of C(9, 3) = 84. P(at least one woman) = 1 − 10/84 = 74/84 = 37/42.",
  },
  {
    id: 20,
    type: "numeric",
    question: "Pipe A alone can fill a tank in 12 hours, and pipe B alone can fill it in 18 hours. Both pipes are opened together on an empty tank, and after 4 hours pipe A is closed. How many more hours will it take pipe B alone to finish filling the tank?",
    answer: 8,
    explanation: "Together the pipes fill 1/12 + 1/18 = 5/36 of the tank per hour, so in 4 hours they fill 20/36 = 5/9. The remaining 4/9 at pipe B's rate of 1/18 per hour takes (4/9) × 18 = 8 hours.",
  },
  {
    id: 21,
    question: "How many positive integers less than 1,000 have digits whose sum is 3?",
    options: ["6", "9", "10", "12", "15"],
    answer: "10",
    explanation: "Write each number as three digits a, b, c (leading zeros allowed) with a + b + c = 3. The number of non-negative solutions is C(5, 2) = 10: 3, 12, 21, 30, 102, 111, 120, 201, 210, 300.",
  },
  {
    id: 22,
    question: "If f(x) = x² − 4x + c and the minimum value of f(x) is 5, what is the value of f(−1)?",
    options: ["10", "12", "14", "16", "18"],
    answer: "14",
    explanation: "The minimum of x² − 4x + c occurs at x = 2: 4 − 8 + c = 5, so c = 9. Then f(−1) = 1 + 4 + 9 = 14.",
  },
  {
    id: 23,
    type: "multi",
    question: "If n is a positive integer, which of the following could be the units digit of 7ⁿ + 3ⁿ?",
    options: ["0", "2", "4", "6", "8"],
    answer: ["0", "2", "8"],
    explanation: "Units digits of 7ⁿ repeat 7, 9, 3, 1 and of 3ⁿ repeat 3, 9, 7, 1. The sums for n = 1, 2, 3, 4 are 7 + 3 = 10, 9 + 9 = 18, 3 + 7 = 10 and 1 + 1 = 2, giving units digits 0, 8, 0 and 2.",
  },
  {
    id: 24,
    type: "numeric",
    question: "In an arithmetic sequence, the 5th term is 17 and the 12th term is 45. What is the 20th term?",
    answer: 77,
    explanation: "From the 5th to the 12th term there are 7 steps: 7d = 45 − 17 = 28, so d = 4. The 20th term is 45 + 8 × 4 = 77.",
  },
  {
    id: 25,
    question: "In the xy-plane, line ℓ passes through the point (2, 5) and is perpendicular to the line 3x − 4y = 12. What is the y-intercept of line ℓ?",
    options: ["−1/3", "7/3", "13/3", "17/3", "23/3"],
    answer: "23/3",
    explanation: "3x − 4y = 12 has slope 3/4, so ℓ has slope −4/3. y − 5 = −(4/3)(x − 2) gives y = −(4/3)x + 8/3 + 5 = −(4/3)x + 23/3.",
  },
  {
    id: 26,
    question: "Solution X is 20 percent acid and solution Y is 50 percent acid. How many liters of solution Y must be added to 30 liters of solution X to produce a solution that is 30 percent acid?",
    options: ["10", "12", "15", "18", "20"],
    answer: "15",
    explanation: "Acid balance: 0.2(30) + 0.5y = 0.3(30 + y), so 6 + 0.5y = 9 + 0.3y and y = 15. Check: 6 + 7.5 = 13.5 = 0.3 × 45.",
  },
  {
    id: 27,
    type: "numeric",
    question: "What is the least positive integer k for which 360k is the cube of an integer?",
    answer: 75,
    explanation: "360 = 2³ × 3² × 5. For a cube every exponent must be a multiple of 3, so k must supply one more 3 and two more 5s: k = 3 × 5² = 75 (360 × 75 = 27,000 = 30³).",
  },
];
